import { SlateDateRange, SlateEntities, SlateMetricFocus, SlateTopCriteria, SlateUserListType } from './slate.types';
import {
    extractFilmTitleQuery,
    extractMetricFocus as pureMetricFocus,
    extractWantAllMetrics as pureWantAll,
    extractWantTopTable as pureWantTop,
} from './slate-nlp.pure';

const MONTHS: Record<string, number> = {
    jan: 0,
    january: 0,
    feb: 1,
    february: 1,
    mar: 2,
    march: 2,
    apr: 3,
    april: 3,
    may: 4,
    jun: 5,
    june: 5,
    jul: 6,
    july: 6,
    aug: 7,
    august: 7,
    sep: 8,
    sept: 8,
    september: 8,
    oct: 9,
    october: 9,
    nov: 10,
    november: 10,
    dec: 11,
    december: 11,
};

const DEFAULT_LIMIT = 5;
const MAX_LIMIT = 25;

function pad(n: number): string {
    return String(n).padStart(2, '0');
}

export function formatYmd(d: Date): string {
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function startOfDay(d: Date): Date {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
}

function endOfDay(d: Date): Date {
    const x = new Date(d);
    x.setHours(23, 59, 59, 999);
    return x;
}

function labelRange(start: Date, end: Date): string {
    const fmt = (d: Date) =>
        d.toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
        });
    return `${fmt(start)} – ${fmt(end)}`;
}

function lastNDaysRange(days: number, inferredDefault = false): SlateDateRange {
    // Match Studio DateRangePicker.getLastNDays(days):
    // start = today - days (e.g. last 7 days on Aug 13 → Aug 6–13)
    const end = endOfDay(new Date());
    const start = startOfDay(new Date());
    start.setDate(start.getDate() - days);
    return {
        startDate: formatYmd(start),
        endDate: formatYmd(end),
        label: labelRange(start, end),
        inferredDefault,
    };
}

function defaultLast30(): SlateDateRange {
    return lastNDaysRange(30, true);
}

function parseDayMonthYear(
    day: number,
    month: number,
    year: number
): Date | null {
    if (month < 0 || month > 11 || day < 1 || day > 31) return null;
    const d = new Date(year, month, day);
    if (d.getMonth() !== month) return null;
    return d;
}

export class SlateEntityExtractor {
    extract(text: string, opts?: { requireDate?: boolean }): SlateEntities {
        const normalized = text.toLowerCase().trim();
        const listAll =
            /\ball\b/.test(normalized) &&
            /\b(films?|movies?|series|web\s*series)\b/.test(normalized);
        const listAsk =
            /\b(list|show|my)\b/.test(normalized) &&
            /\b(films?|movies?|series|web\s*series)\b/.test(normalized);
        const skipDefaultDate =
            opts?.requireDate === false || listAll || (listAsk && !/\b(last|this|from|between|yesterday|today|week|month|year|days?)\b/.test(normalized));

        const dateRange =
            this.extractDateRange(normalized) ??
            (skipDefaultDate ? undefined : defaultLast30());

        let limit = this.extractLimit(normalized);
        if (listAll && !this.extractLimitOnly(normalized)) {
            limit = Math.min(50, 100);
        }

        return {
            dateRange,
            limit,
            topCriteria: this.extractTopCriteria(normalized),
            matchParams: this.extractSearch(normalized),
            filmTitleQuery: extractFilmTitleQuery(text),
            status: this.extractStatus(normalized),
            userListType: this.extractUserListType(normalized),
            groupBy: this.extractGroupBy(normalized),
            metricFocus: this.extractMetricFocus(normalized),
            wantTopTable: this.extractWantTopTable(normalized),
            wantAllMetrics: this.extractWantAllMetrics(normalized),
        };
    }

    extractLimitOnly(text: string): number | null {
        const m = text.toLowerCase().match(/\btop\s+(\d+)\b|\b(\d+)\s+(?:rows|items|films|titles)\b/);
        if (!m) return null;
        const n = Number(m[1] || m[2]);
        if (!Number.isFinite(n) || n < 1) return null;
        return Math.min(Math.floor(n), MAX_LIMIT);
    }

    private extractLimit(text: string): number {
        return this.extractLimitOnly(text) ?? DEFAULT_LIMIT;
    }

    private extractWantAllMetrics(text: string): boolean {
        return pureWantAll(text);
    }

    private extractWantTopTable(text: string): boolean {
        return pureWantTop(text);
    }

    private extractMetricFocus(text: string): SlateMetricFocus {
        return pureMetricFocus(text);
    }

    private extractTopCriteria(text: string): SlateTopCriteria | undefined {
        if (/unique\s*views?|unique viewers?/.test(text)) return 'uniqueViews';
        if (
            /minutes?\s*watched|watched\s*minutes?|watch\s*minutes?|watching\s*minutes?|watch\s*time|watchtime|time\s*watched|viewing\s*(?:time|minutes?)|play(?:back)?\s*time|how\s+many\s+(?:watch\s*)?minutes?|hours?\s*watched/.test(
                text
            )
        )
            return 'minutesWatched';
        if (
            /views?|plays?|watch\s*count|times?\s+watched|view count|play count|number of views?/.test(
                text
            )
        )
            return 'views';
        return undefined;
    }

    private extractGroupBy(
        text: string
    ): 'day' | 'week' | 'month' | 'year' | undefined {
        if (/\bby\s+year|yearly\b/.test(text)) return 'year';
        if (/\bby\s+month|monthly\b/.test(text)) return 'month';
        if (/\bby\s+week|weekly\b/.test(text)) return 'week';
        if (/\bby\s+day|daily\b/.test(text)) return 'day';
        return undefined;
    }

    private extractStatus(text: string): string | undefined {
        // Studio list APIs expect Title Case (Approved), not APPROVED
        // Ready-for-approve phrasing BEFORE bare "approved"
        if (
            /\bready\s+(?:for|to)\s+approv/i.test(text) ||
            /\b(?:awaiting|waiting\s+for)\s+approv/i.test(text) ||
            /\bpending\s+approv/i.test(text) ||
            /\bfor\s+approv(?:al|e)\b/i.test(text) ||
            /\bneed(?:s)?\s+approv/i.test(text)
        ) {
            return 'Submitted';
        }
        if (/\bin\s*review\b/.test(text)) return 'In Review';
        if (/\bpending\b/.test(text)) return 'Pending';
        if (/\bapproved\b/.test(text)) return 'Approved';
        if (/\brejected\b|\bdeclined\b/.test(text)) return 'Declined';
        if (/\bsubmitted\b/.test(text)) return 'Submitted';
        if (/\bdraft(ed)?\b/.test(text)) return 'Draft';
        if (/\bsuspended\b/.test(text)) return 'Suspended';
        if (/\bpublished\b/.test(text)) return 'Published';
        return undefined;
    }

    private extractUserListType(text: string): SlateUserListType | undefined {
        if (/content\s*curator|curators?\b/.test(text)) return 'CONTENT_CURATOR';
        if (/channel\s*partner|agents?\b/.test(text)) return 'AGENT';
        if (/filmmakers?|film\s*makers?/.test(text)) return 'FILM_MAKER';
        if (/viewers?\b|audience\b/.test(text)) return 'VIEWER';
        if (/admins?\b|staff\b/.test(text)) return 'ADMIN';
        return undefined;
    }

    private extractSearch(text: string): string | undefined {
        const quoted = text.match(/["']([^"']+)["']/);
        if (quoted?.[1]) return quoted[1].trim();

        const searchFor = text.match(
            /(?:search|find|named|title|called)\s+(.+?)(?:\s+(?:from|between|last|this|top)\b|$)/i
        );
        if (searchFor?.[1]) {
            const cleaned = searchFor[1]
                .replace(/\b(films?|movies?|web\s*series|titles?)\b/gi, '')
                .trim();
            if (cleaned.length >= 2) return cleaned;
        }
        return undefined;
    }

    extractDateRange(text: string): SlateDateRange | null {
        const now = new Date();

        if (/\btoday\b/.test(text)) {
            const d = startOfDay(now);
            return {
                startDate: formatYmd(d),
                endDate: formatYmd(d),
                label: labelRange(d, d),
            };
        }

        if (/\byesterday\b/.test(text)) {
            const d = startOfDay(now);
            d.setDate(d.getDate() - 1);
            return {
                startDate: formatYmd(d),
                endDate: formatYmd(d),
                label: labelRange(d, d),
            };
        }

        const lastN = text.match(/\blast\s+(\d+)\s+days?\b/);
        if (lastN) {
            const n = Number(lastN[1]);
            return lastNDaysRange(n);
        }

        if (/\blast\s+week\b|past\s+week\b/.test(text)) {
            // Same window as Studio "Last 7 days" quick range
            return lastNDaysRange(7);
        }

        if (/\blast\s+month\b|previous\s+month\b/.test(text)) {
            const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            const end = new Date(now.getFullYear(), now.getMonth(), 0);
            return {
                startDate: formatYmd(startOfDay(start)),
                endDate: formatYmd(endOfDay(end)),
                label: labelRange(start, end),
            };
        }

        if (/\bthis\s+week\b/.test(text)) {
            const start = startOfDay(now);
            const day = start.getDay();
            const diff = day === 0 ? 6 : day - 1;
            start.setDate(start.getDate() - diff);
            const end = endOfDay(now);
            return {
                startDate: formatYmd(start),
                endDate: formatYmd(end),
                label: labelRange(start, end),
            };
        }

        if (/\bthis\s+month\b/.test(text)) {
            const start = new Date(now.getFullYear(), now.getMonth(), 1);
            const end = endOfDay(now);
            return {
                startDate: formatYmd(startOfDay(start)),
                endDate: formatYmd(end),
                label: labelRange(start, end),
            };
        }

        if (/\bthis\s+year\b/.test(text)) {
            const start = new Date(now.getFullYear(), 0, 1);
            const end = endOfDay(now);
            return {
                startDate: formatYmd(startOfDay(start)),
                endDate: formatYmd(end),
                label: labelRange(start, end),
            };
        }

        if (/\blast\s+30\s+days?\b|\bpast\s+30\s+days?\b/.test(text)) {
            return lastNDaysRange(30);
        }

        if (/\blast\s+31\s+days?\b|\bpast\s+31\s+days?\b/.test(text)) {
            return lastNDaysRange(31);
        }

        // Single day BEFORE month-year (else "10th August 2026" becomes whole August)
        const singleDay = this.extractSingleDay(text, now);
        if (singleDay) return singleDay;

        // Month year: "aug 2026" — single-day patterns already handled above
        const monthYear = text.match(
            /\b(jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\s+(\d{4})\b/
        );
        if (monthYear) {
            // Skip if this month token is part of "10th August 2026" (day already handled)
            const before = text.slice(
                Math.max(0, monthYear.index! - 8),
                monthYear.index!
            );
            if (!/\d(?:st|nd|rd|th)?\s*$/i.test(before)) {
                const month = MONTHS[monthYear[1]];
                const year = Number(monthYear[2]);
                const start = new Date(year, month, 1);
                const end = new Date(year, month + 1, 0);
                return {
                    startDate: formatYmd(startOfDay(start)),
                    endDate: formatYmd(endOfDay(end)),
                    label: labelRange(start, end),
                };
            }
        }

        // from X to Y / between X and Y
        const between = text.match(
            /(?:from|between)\s+(.+?)\s+(?:to|and|-|–)\s+(.+?)(?:\s|$)/i
        );
        if (between) {
            const a = this.parseFlexibleDate(between[1], now);
            const b = this.parseFlexibleDate(between[2], now);
            if (a && b) {
                const start = a <= b ? a : b;
                const end = a <= b ? b : a;
                return {
                    startDate: formatYmd(startOfDay(start)),
                    endDate: formatYmd(endOfDay(end)),
                    label: labelRange(start, end),
                };
            }
        }

        // ISO or numeric ranges: 2026-08-01 to 2026-08-10 / 1/8/2026 - 10/8/2026
        const numericRange = text.match(
            /(\d{1,4}[\/\-.]\d{1,2}[\/\-.]\d{1,4})\s*(?:to|-|–)\s*(\d{1,4}[\/\-.]\d{1,2}[\/\-.]\d{1,4})/
        );
        if (numericRange) {
            const a = this.parseFlexibleDate(numericRange[1], now);
            const b = this.parseFlexibleDate(numericRange[2], now);
            if (a && b) {
                const start = a <= b ? a : b;
                const end = a <= b ? b : a;
                return {
                    startDate: formatYmd(startOfDay(start)),
                    endDate: formatYmd(endOfDay(end)),
                    label: labelRange(start, end),
                };
            }
        }

        return null;
    }

    /** "on 10th August 2026", "10 Aug 2026", "August 10 2026", "10/8/2026" */
    private extractSingleDay(text: string, now: Date): SlateDateRange | null {
        const month =
            'jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?';

        const dmyNamed = text.match(
            new RegExp(
                `\\b(?:on\\s+|for\\s+|dated\\s+)?(\\d{1,2})(?:st|nd|rd|th)?\\s+(${month})(?:\\s*,?\\s*(\\d{4}))?\\b`,
                'i'
            )
        );
        if (dmyNamed) {
            const year = dmyNamed[3]
                ? Number(dmyNamed[3])
                : now.getFullYear();
            const d = parseDayMonthYear(
                Number(dmyNamed[1]),
                MONTHS[dmyNamed[2].toLowerCase()],
                year
            );
            if (d) {
                return {
                    startDate: formatYmd(startOfDay(d)),
                    endDate: formatYmd(endOfDay(d)),
                    label: labelRange(d, d),
                };
            }
        }

        const mdyNamed = text.match(
            new RegExp(
                `\\b(?:on\\s+|for\\s+)?(${month})\\s+(\\d{1,2})(?:st|nd|rd|th)?(?:\\s*,?\\s*(\\d{4}))?\\b`,
                'i'
            )
        );
        if (mdyNamed) {
            const year = mdyNamed[3]
                ? Number(mdyNamed[3])
                : now.getFullYear();
            const d = parseDayMonthYear(
                Number(mdyNamed[2]),
                MONTHS[mdyNamed[1].toLowerCase()],
                year
            );
            if (d) {
                return {
                    startDate: formatYmd(startOfDay(d)),
                    endDate: formatYmd(endOfDay(d)),
                    label: labelRange(d, d),
                };
            }
        }

        // lone ISO / en-GB date in sentence
        const iso = text.match(/\b(\d{4}-\d{1,2}-\d{1,2})\b/);
        if (iso) {
            const d = this.parseFlexibleDate(iso[1], now);
            if (d) {
                return {
                    startDate: formatYmd(startOfDay(d)),
                    endDate: formatYmd(endOfDay(d)),
                    label: labelRange(d, d),
                };
            }
        }

        const dmy = text.match(/\b(\d{1,2}[\/\-.]\d{1,2}[\/\-.]\d{2,4})\b/);
        if (dmy && !/\bto\b|\b-\b|\b–\b/.test(text)) {
            const d = this.parseFlexibleDate(dmy[1], now);
            if (d) {
                return {
                    startDate: formatYmd(startOfDay(d)),
                    endDate: formatYmd(endOfDay(d)),
                    label: labelRange(d, d),
                };
            }
        }

        return null;
    }

    private parseFlexibleDate(raw: string, now: Date): Date | null {
        const s = raw.trim().toLowerCase().replace(/,/g, '');

        // yyyy-mm-dd
        const iso = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
        if (iso) {
            return parseDayMonthYear(
                Number(iso[3]),
                Number(iso[2]) - 1,
                Number(iso[1])
            );
        }

        // dd/mm/yyyy or dd-mm-yyyy (en-GB)
        const dmy = s.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{2,4})$/);
        if (dmy) {
            let year = Number(dmy[3]);
            if (year < 100) year += 2000;
            return parseDayMonthYear(Number(dmy[1]), Number(dmy[2]) - 1, year);
        }

        // 1 aug / 1 aug 2026 / aug 1 2026
        const dMonY = s.match(
            /^(\d{1,2})\s+(jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)(?:\s+(\d{4}))?$/
        );
        if (dMonY) {
            const year = dMonY[3] ? Number(dMonY[3]) : now.getFullYear();
            return parseDayMonthYear(
                Number(dMonY[1]),
                MONTHS[dMonY[2]],
                year
            );
        }

        const monDY = s.match(
            /^(jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\s+(\d{1,2})(?:\s+(\d{4}))?$/
        );
        if (monDY) {
            const year = monDY[3] ? Number(monDY[3]) : now.getFullYear();
            return parseDayMonthYear(
                Number(monDY[2]),
                MONTHS[monDY[1]],
                year
            );
        }

        return null;
    }
}

export const SLATE_DEFAULT_LIMIT = DEFAULT_LIMIT;
export const SLATE_MAX_LIMIT = MAX_LIMIT;
